import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronRight, ChevronLeft, Upload, X, Copy, Key } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectors = [
  'Fintech', 'EdTech', 'HealthTech', 'Sustainability', 'AgriTech',
  'Craft & Creative', 'SaaS', 'E-Commerce', 'Social Impact', 'Other'
];

const stages = [
  'Idea', 'Validation', 'Prototype', 'MVP', 'Early Revenue', 'Scaling'
];

const helpOptions = [
  'Mentorship', 'Funding Access', 'Legal & Compliance', 'Technical Development',
  'Marketing & Growth', 'Networking & Introductions', 'Co-Founder Matching', 'Workspace', 'Other'
];

const affiliations = ['Current Student', 'Alumni', 'Faculty', 'Other'];

const referralSources = [
  'Campus event', 'Social media', 'Friend or peer', 'Faculty referral', 'Website', 'Other'
];

const formSteps = [
  { id: 1, title: 'Founder Info', description: 'Tell us about yourself' },
  { id: 2, title: 'Startup Info', description: 'About your venture' },
  { id: 3, title: 'Problem & Solution', description: 'What you\'re solving' },
  { id: 4, title: 'Progress', description: 'Where you are now' },
  { id: 5, title: 'Help Needed', description: 'How we can help' }
];

export default function RegisterStartupPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    // Founder Info
    founder_name: '',
    founder_email: '',
    founder_phone: '',
    affiliation: '',
    batch_year: '',
    linkedin_url: '',
    // Startup Info
    startup_name: '',
    startup_logo_url: '',
    tagline: '',
    detailed_description: '',
    website_url: '',
    social_links: [],
    // Problem & Solution
    problem: '',
    target_customer: '',
    solution: '',
    // Stage & Progress
    stage: '',
    milestones: '',
    has_product: false,
    team_size: 1,
    team_members: '',
    // Business Model
    revenue_model: '',
    has_revenue: false,
    revenue_amount: '',
    // Sector
    sector: '',
    // Help Needed
    help_needed: [],
    help_details: '',
    // Additional
    referral_source: '',
    additional_info: '',
    consent: false,
    // Registration Code (6-digit daily code from CfE)
    registration_code: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleHelpNeeded = (option) => {
    setFormData(prev => ({
      ...prev,
      help_needed: prev.help_needed.includes(option)
        ? prev.help_needed.filter(h => h !== option)
        : [...prev.help_needed, option]
    }));
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Please upload a PNG or JPG image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }
    
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const removeLogo = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    updateFormData('startup_logo_url', '');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.founder_name && formData.founder_email && formData.affiliation;
      case 2:
        return formData.startup_name && formData.tagline && formData.detailed_description;
      case 3:
        return formData.problem && formData.target_customer && formData.solution;
      case 4:
        return formData.stage && formData.sector;
      case 5:
        return formData.help_needed.length > 0 && formData.consent && formData.registration_code.length === 6;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      toast.error('Please complete all required fields and accept the consent');
      return;
    }

    setSubmitting(true);
    try {
      let logoUrl = formData.startup_logo_url;
      
      // Upload logo file if selected
      if (logoFile) {
        setUploadingLogo(true);
        const uploadData = new FormData();
        uploadData.append('file', logoFile);
        const uploadRes = await axios.post(`${API}/upload/logo`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        logoUrl = uploadRes.data.path;
        setUploadingLogo(false);
      }
      
      const payload = { ...formData, startup_logo_url: logoUrl };
      const response = await axios.post(`${API}/startup-registrations`, payload);
      setRegistrationResult(response.data);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      setUploadingLogo(false);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(e => e.msg).join(', ')
          : 'Please try again';
      toast.error('Submission failed', { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (submitted && registrationResult) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F3F4F6] p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center"
        >
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#231F20] mb-4 font-['Bricolage_Grotesque']">
                Registration Successful!
              </h1>
              <p className="text-[#58595B] mb-8">
                Welcome to the CfE ecosystem! Save your login credentials below to book advisor sessions.
              </p>
              
              {/* Credentials Card */}
              <div className="bg-[#075FA3]/5 border-2 border-[#075FA3] rounded-xl p-6 mb-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Key className="w-5 h-5 text-[#075FA3]" />
                  <h3 className="font-semibold text-[#075FA3]">Your Login Credentials</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-[#58595B] mb-1">Startup Name (Username)</p>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-[#231F20] text-lg">{registrationResult.startup_name}</p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(registrationResult.startup_name)}
                        className="text-[#075FA3]"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-[#58595B] mb-1">4-Digit PIN</p>
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-[#C83041] text-3xl tracking-[0.5em]">{registrationResult.pin}</p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyToClipboard(registrationResult.pin)}
                        className="text-[#075FA3]"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-[#C83041] mt-4 font-medium">
                  ⚠️ Save these credentials! You'll need them to book advisor sessions.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Link to="/advisors/login">
                  <Button className="w-full bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full py-6">
                    Book an Advisor Now
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full rounded-full py-6">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-[#C83041] relative overflow-hidden" data-testid="register-hero">
        <div className="absolute inset-0 subtle-gradient opacity-20" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Bricolage_Grotesque']"
              data-testid="register-heading"
            >
              Register Your Startup with CfE
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90"
            >
              Join the CfE ecosystem. Tell us about your venture so we can understand 
              where you are, what you need, and how we can help.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-[#F3F4F6]" data-testid="register-form">
        <div className="container-main">
          {/* Progress Bar */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {formSteps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      currentStep > step.id 
                        ? 'bg-green-500 text-white' 
                        : currentStep === step.id 
                          ? 'bg-[#075FA3] text-white' 
                          : 'bg-white text-gray-400 border border-gray-200'
                    }`}>
                      {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                    </div>
                    <span className={`text-xs mt-2 hidden md:block ${
                      currentStep >= step.id ? 'text-[#231F20]' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < formSteps.length - 1 && (
                    <div className={`w-12 md:w-24 h-1 mx-2 transition-colors ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <Card className="max-w-3xl mx-auto">
            <CardContent className="p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">
                  {formSteps[currentStep - 1].title}
                </h2>
                <p className="text-[#58595B]">{formSteps[currentStep - 1].description}</p>
              </div>

              {/* Step 1: Founder Info */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Full Name *</Label>
                      <Input
                        value={formData.founder_name}
                        onChange={(e) => updateFormData('founder_name', e.target.value)}
                        className="form-input"
                        data-testid="founder-name-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Email Address *</Label>
                      <Input
                        type="email"
                        value={formData.founder_email}
                        onChange={(e) => updateFormData('founder_email', e.target.value)}
                        className="form-input"
                        data-testid="founder-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Phone Number</Label>
                      <Input
                        type="tel"
                        value={formData.founder_phone}
                        onChange={(e) => updateFormData('founder_phone', e.target.value)}
                        className="form-input"
                        data-testid="founder-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Krea Affiliation *</Label>
                      <Select value={formData.affiliation} onValueChange={(v) => updateFormData('affiliation', v)}>
                        <SelectTrigger className="form-input" data-testid="affiliation-select">
                          <SelectValue placeholder="Select affiliation" />
                        </SelectTrigger>
                        <SelectContent>
                          {affiliations.map(a => (
                            <SelectItem key={a} value={a}>{a}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Year / Batch</Label>
                      <Input
                        value={formData.batch_year}
                        onChange={(e) => updateFormData('batch_year', e.target.value)}
                        placeholder="e.g., 2024"
                        className="form-input"
                        data-testid="batch-year-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">LinkedIn Profile URL</Label>
                      <Input
                        type="url"
                        value={formData.linkedin_url}
                        onChange={(e) => updateFormData('linkedin_url', e.target.value)}
                        className="form-input"
                        data-testid="linkedin-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Startup Info */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <Label className="form-label">Startup Name *</Label>
                    <Input
                      value={formData.startup_name}
                      onChange={(e) => updateFormData('startup_name', e.target.value)}
                      className="form-input"
                      data-testid="startup-name-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">One-Line Description / Tagline *</Label>
                    <Input
                      value={formData.tagline}
                      onChange={(e) => updateFormData('tagline', e.target.value)}
                      placeholder="A brief, catchy description of your startup"
                      className="form-input"
                      data-testid="tagline-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">About *</Label>
                    <Textarea
                      value={formData.detailed_description}
                      onChange={(e) => updateFormData('detailed_description', e.target.value)}
                      placeholder="What does your startup do? Explain in detail."
                      className="form-input min-h-[120px]"
                      data-testid="description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Website URL</Label>
                      <Input
                        type="url"
                        value={formData.website_url}
                        onChange={(e) => updateFormData('website_url', e.target.value)}
                        className="form-input"
                        data-testid="website-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Startup Logo (PNG or JPG)</Label>
                      {logoPreview ? (
                        <div className="flex items-center gap-4 mt-1">
                          <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-white" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#231F20] truncate">{logoFile?.name}</p>
                            <p className="text-xs text-[#58595B]">{(logoFile?.size / 1024).toFixed(0)} KB</p>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={removeLogo} className="text-red-500 shrink-0" data-testid="remove-logo-btn">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label
                          className="flex flex-col items-center justify-center w-full h-24 mt-1 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#075FA3] hover:bg-[#075FA3]/5 transition-colors"
                          data-testid="logo-upload-area"
                        >
                          <Upload className="w-5 h-5 text-[#58595B] mb-1" />
                          <span className="text-sm text-[#58595B]">Click to upload logo</span>
                          <span className="text-xs text-gray-400">PNG or JPG, max 5MB</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleLogoSelect}
                            className="hidden"
                            data-testid="logo-file-input"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Problem & Solution */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <Label className="form-label">What problem are you solving? *</Label>
                    <Textarea
                      value={formData.problem}
                      onChange={(e) => updateFormData('problem', e.target.value)}
                      placeholder="Describe the problem your startup addresses"
                      className="form-input min-h-[100px]"
                      data-testid="problem-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Who is your target customer/user? *</Label>
                    <Textarea
                      value={formData.target_customer}
                      onChange={(e) => updateFormData('target_customer', e.target.value)}
                      placeholder="Describe your ideal customer or user"
                      className="form-input min-h-[100px]"
                      data-testid="customer-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">What is your solution? *</Label>
                    <Textarea
                      value={formData.solution}
                      onChange={(e) => updateFormData('solution', e.target.value)}
                      placeholder="Explain how your startup solves the problem"
                      className="form-input min-h-[100px]"
                      data-testid="solution-input"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Progress */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Current Stage *</Label>
                      <Select value={formData.stage} onValueChange={(v) => updateFormData('stage', v)}>
                        <SelectTrigger className="form-input" data-testid="stage-select">
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">Sector / Industry *</Label>
                      <Select value={formData.sector} onValueChange={(v) => updateFormData('sector', v)}>
                        <SelectTrigger className="form-input" data-testid="sector-select">
                          <SelectValue placeholder="Select sector" />
                        </SelectTrigger>
                        <SelectContent>
                          {sectors.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Key Milestones Achieved</Label>
                    <Textarea
                      value={formData.milestones}
                      onChange={(e) => updateFormData('milestones', e.target.value)}
                      placeholder="What have you accomplished so far?"
                      className="form-input min-h-[100px]"
                      data-testid="milestones-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Number of Team Members</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.team_size}
                        onChange={(e) => updateFormData('team_size', parseInt(e.target.value) || 1)}
                        className="form-input"
                        data-testid="team-size-input"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-8">
                      <Checkbox
                        id="has_product"
                        checked={formData.has_product}
                        onCheckedChange={(checked) => updateFormData('has_product', checked)}
                        data-testid="has-product-checkbox"
                      />
                      <Label htmlFor="has_product" className="text-sm text-[#231F20] cursor-pointer">
                        Do you have a working product/prototype?
                      </Label>
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Team Members' Names & Roles</Label>
                    <Textarea
                      value={formData.team_members}
                      onChange={(e) => updateFormData('team_members', e.target.value)}
                      placeholder="e.g., John Doe - CEO, Jane Smith - CTO"
                      className="form-input"
                      data-testid="team-members-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Revenue Model</Label>
                    <Textarea
                      value={formData.revenue_model}
                      onChange={(e) => updateFormData('revenue_model', e.target.value)}
                      placeholder="How do you plan to make money?"
                      className="form-input"
                      data-testid="revenue-model-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="has_revenue"
                        checked={formData.has_revenue}
                        onCheckedChange={(checked) => updateFormData('has_revenue', checked)}
                        data-testid="has-revenue-checkbox"
                      />
                      <Label htmlFor="has_revenue" className="text-sm text-[#231F20] cursor-pointer">
                        Have you generated any revenue yet?
                      </Label>
                    </div>
                    {formData.has_revenue && (
                      <div>
                        <Label className="form-label">Approximate Revenue</Label>
                        <Input
                          value={formData.revenue_amount}
                          onChange={(e) => updateFormData('revenue_amount', e.target.value)}
                          placeholder="e.g., ₹50,000"
                          className="form-input"
                          data-testid="revenue-amount-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Help Needed */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div>
                    <Label className="form-label">What kind of support do you need from CfE? *</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                      {helpOptions.map((option) => (
                        <div
                          key={option}
                          onClick={() => toggleHelpNeeded(option)}
                          className={`p-3 rounded-xl border cursor-pointer transition-all ${
                            formData.help_needed.includes(option)
                              ? 'border-[#075FA3] bg-[#075FA3]/10 text-[#075FA3]'
                              : 'border-gray-200 hover:border-[#075FA3]/50'
                          }`}
                          data-testid={`help-option-${option.toLowerCase().replace(/\s|&/g, '-')}`}
                        >
                          <span className="text-sm font-medium">{option}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Describe your needs in more detail</Label>
                    <Textarea
                      value={formData.help_details}
                      onChange={(e) => updateFormData('help_details', e.target.value)}
                      placeholder="Tell us more about what specific help you're looking for"
                      className="form-input min-h-[100px]"
                      data-testid="help-details-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">How did you hear about CfE?</Label>
                    <Select value={formData.referral_source} onValueChange={(v) => updateFormData('referral_source', v)}>
                      <SelectTrigger className="form-input" data-testid="referral-select">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {referralSources.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">Anything else you'd like us to know?</Label>
                    <Textarea
                      value={formData.additional_info}
                      onChange={(e) => updateFormData('additional_info', e.target.value)}
                      className="form-input"
                      data-testid="additional-info-input"
                    />
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-[#F3F4F6] rounded-xl">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => updateFormData('consent', checked)}
                      data-testid="consent-checkbox"
                    />
                    <Label htmlFor="consent" className="text-sm text-[#58595B] cursor-pointer">
                      I agree that this information will be stored and used by the Centre for 
                      Entrepreneurship, Krea University, for incubation and support purposes. *
                    </Label>
                  </div>
                  
                  {/* 6-Digit Registration Code */}
                  <div className="bg-[#C83041]/5 border-2 border-[#C83041] rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-[#C83041]" />
                      <Label className="font-semibold text-[#C83041]">Registration Code *</Label>
                    </div>
                    <p className="text-sm text-[#58595B] mb-4">
                      Enter the 6-digit registration code provided by the CfE team to complete your registration.
                    </p>
                    <Input
                      type="text"
                      value={formData.registration_code}
                      onChange={(e) => updateFormData('registration_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="• • • • • •"
                      maxLength={6}
                      className="form-input text-center text-2xl tracking-[0.5em] font-bold"
                      data-testid="registration-code-input"
                    />
                    <p className="text-xs text-[#58595B] mt-2">
                      Don't have a code? Contact the CfE team to get today's registration code.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-10 pt-6 border-t border-gray-100">
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="rounded-full px-8 py-6"
                    data-testid="back-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                {currentStep < 5 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full px-8 py-6"
                    data-testid="next-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !formData.consent}
                    className="bg-[#C83041] hover:bg-[#A92332] text-white rounded-full px-8 py-6"
                    data-testid="submit-btn"
                  >
                    {submitting ? (uploadingLogo ? 'Uploading Logo...' : 'Submitting...') : 'Submit Registration'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
